import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Container, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import {
  createClinicCertificate,
  createClinicCertificateWoLoader,
  getClinicCertificates,
  updateClinicCertificateById,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import {
  CERT_INSERT_FIELDS,
  DEFAULT_FOOTER_NOTE,
  STANDARD_CERTIFICATES,
  loadCustomCertificates,
  saveCustomCertificates,
} from "./certificateConstants";
import CertificatePreviewModal from "./CertificatePreviewModal";
import "./Certificates.css";

const initForm = {
  id: null,
  certificateId: null,
  name: "",
  linkedServices: [],
  note: DEFAULT_FOOTER_NOTE,
  body: "",
  kind: "custom",
  subtitle: "Not linked to a service",
  rabiesSourceService: null,
  parasiteSourceService: null,
};

const getApiCertificateId = (cert) => cert?.certificateId ?? cert?.id ?? null;

const HIDDEN_CERTIFICATE_NAMES = new Set(
  [
    "Euthanasia Certificate",
    "Microchip Certificate",
    "Sterilization Certificate",
    "Travel / Export Health Certificate",
    "Rabies Certificate",
    "Fit to Travel Certificate",
    "Fitness / Health Certificate",
  ].map((name) => name.trim().toLowerCase()),
);

const getCertificateName = (cert) =>
  String(cert?.name ?? cert?.certificateName ?? "")
    .trim()
    .toLowerCase();

const isSkippedCertificate = (cert) =>
  HIDDEN_CERTIFICATE_NAMES.has(getCertificateName(cert));

const filterSkippedCertificates = (certs = []) =>
  (certs ?? []).filter((cert) => !isSkippedCertificate(cert));

const sortCertificatesById = (certs = []) =>
  [...(certs ?? [])].sort(
    (a, b) =>
      Number(getApiCertificateId(a) ?? 0) - Number(getApiCertificateId(b) ?? 0),
  );

const mapLinkedServicesFromApi = (linkedServices, serviceOptions) =>
  (linkedServices ?? []).map((entry) => {
    const serviceId =
      typeof entry === "object"
        ? (entry?.serviceId ?? entry?.id ?? entry?.value)
        : entry;
    const match = (serviceOptions ?? []).find(
      (opt) => String(opt?.serviceId ?? opt?.value) === String(serviceId),
    );
    if (match) return match;
    const name =
      typeof entry === "object"
        ? (entry?.serviceName ?? entry?.name ?? String(serviceId))
        : String(serviceId);
    return {
      name,
      serviceName: name,
      value: serviceId,
      serviceId,
      category: "",
      subCategory: "",
    };
  });

const mapServiceByName = (name, serviceOptions) => {
  if (!name) return null;
  const match = (serviceOptions ?? []).find(
    (opt) => opt?.name === name || opt?.serviceName === name,
  );
  return match ?? { name, serviceName: name, value: name, serviceId: name };
};

const toLinkedServiceIds = (linkedServices) =>
  (linkedServices ?? [])
    .map((service) =>
      Number(
        typeof service === "object"
          ? (service?.serviceId ?? service?.value)
          : service,
      ),
    )
    .filter((id) => !Number.isNaN(id) && id > 0);

const findStandardTemplate = (name) =>
  STANDARD_CERTIFICATES.find(
    (cert) =>
      String(cert?.name ?? "")
        .trim()
        .toLowerCase() ===
      String(name ?? "")
        .trim()
        .toLowerCase(),
  );

const mapLinkedServicesFromTemplate = (linkedServices, serviceOptions) =>
  (linkedServices ?? []).map((entry) => {
    const label = entry?.name ?? entry;
    const match = (serviceOptions ?? []).find(
      (opt) =>
        opt?.name === label ||
        String(opt?.value) === String(entry?.value ?? entry),
    );
    return (
      match ?? { name: String(label), value: String(entry?.value ?? label) }
    );
  });

const buildSeedPayloadFromTemplate = (template, serviceOptions) => {
  const linkedServices = mapLinkedServicesFromTemplate(
    template?.linkedServices,
    serviceOptions,
  );
  const payload = {
    name: template?.name ?? "",
    linkedServices: toLinkedServiceIds(linkedServices),
    note: DEFAULT_FOOTER_NOTE,
  };

  if (template?.id === "std-travel-export") {
    payload.rabiesVaccination = template?.rabiesSourceService?.name ?? "";
    payload.parasiteTreatment = template?.parasiteSourceService?.name ?? "";
  }

  return payload;
};

const mapDbCertificateToDisplay = (apiCert, serviceOptions) => {
  const template = findStandardTemplate(apiCert?.name);
  const linkedServices = mapLinkedServicesFromApi(
    apiCert?.linkedServices,
    serviceOptions,
  );
  const subtitle =
    linkedServices.length > 0
      ? linkedServices.map((service) => service?.name).join(", ")
      : (template?.subtitle ?? "Not linked to a service");

  return {
    id: template?.id ?? `db-${getApiCertificateId(apiCert)}`,
    certificateId: getApiCertificateId(apiCert),
    name: apiCert?.name ?? template?.name ?? "",
    linkedServices,
    note: apiCert?.note ?? apiCert?.footerNote ?? DEFAULT_FOOTER_NOTE,
    footerNote: apiCert?.note ?? apiCert?.footerNote ?? DEFAULT_FOOTER_NOTE,
    body: template?.body ?? "",
    kind: template ? "standard" : "custom",
    Icon: template?.Icon,
    subtitle,
    rabiesSourceService: apiCert?.rabiesVaccination
      ? mapServiceByName(apiCert.rabiesVaccination, serviceOptions)
      : (template?.rabiesSourceService ?? null),
    parasiteSourceService: apiCert?.parasiteTreatment
      ? mapServiceByName(apiCert.parasiteTreatment, serviceOptions)
      : (template?.parasiteSourceService ?? null),
  };
};

const buildCertificatePayload = (formValues, isTravelExport) => {
  const payload = {
    name: (formValues?.name ?? "").trim(),
    linkedServices: toLinkedServiceIds(formValues?.linkedServices),
    note: formValues?.note ?? DEFAULT_FOOTER_NOTE,
  };

  if (isTravelExport) {
    payload.rabiesVaccination = formValues?.rabiesSourceService?.name ?? "";
    payload.parasiteTreatment = formValues?.parasiteSourceService?.name ?? "";
  }

  return payload;
};

const Certificates = () => {
  const dispatch = useDispatch();
  const fetchRef = useRef(false);
  const seedingRef = useRef(false);
  const quillRef = useRef(null);
  const clinicServices = useSelector((state) => state?.clinic?.services);
  const apiCertificates = useSelector((state) => state?.clinic?.certificates);
  const [view, setView] = useState("list"); // list | editor
  const [customCerts, setCustomCerts] = useState(() =>
    loadCustomCertificates(),
  );
  const [formValues, setFormValues] = useState(initForm);
  const [insertField, setInsertField] = useState(null);
  const [previewCert, setPreviewCert] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [certsFetched, setCertsFetched] = useState(false);

  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const mandateServices = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return Array.isArray(reqData?.services) ? reqData.services : [];
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  const mandateCertificates = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const certs = Array.isArray(reqData?.certificates)
        ? reqData.certificates
        : [];
      return sortCertificatesById(filterSkippedCertificates(certs));
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  const certificatesSource = useMemo(() => {
    if (certsFetched) {
      return sortCertificatesById(
        filterSkippedCertificates(
          Array.isArray(apiCertificates) ? apiCertificates : [],
        ),
      );
    }
    return mandateCertificates;
  }, [certsFetched, apiCertificates, mandateCertificates]);

  const serviceList = useMemo(() => {
    const source =
      clinicServices?.data?.length > 0
        ? clinicServices.data
        : Array.isArray(clinicServices) && clinicServices.length > 0
          ? clinicServices
          : mandateServices;
    return (source ?? [])
      ?.filter((ser) => ser?.active !== "N")
      ?.map((ser) => ({
        name: ser?.serviceName ?? ser?.name ?? "",
        serviceName: ser?.serviceName ?? ser?.name ?? "",
        value: ser?.serviceId ?? ser?.serviceName ?? ser?.name,
        serviceId: ser?.serviceId,
        category: ser?.category ?? "",
        subCategory: ser?.subCategory ?? "",
      }))
      ?.filter((opt) => opt?.name && opt?.value != null && opt?.value !== "");
  }, [clinicServices, mandateServices]);

  const certificateList = useMemo(
    () =>
      (certificatesSource ?? []).map((cert) =>
        mapDbCertificateToDisplay(cert, serviceList),
      ),
    [certificatesSource, serviceList],
  );

  const syncCertificatesSession = (payload) => {
    const filtered = sortCertificatesById(
      filterSkippedCertificates(Array.isArray(payload) ? payload : []),
    );
    patchMandateSession({
      certificates: filtered,
    });
    setCertsFetched(true);
  };

  const onApiCall = () =>
    dispatch(getClinicCertificates()).then((res) => {
      const payload = res?.payload;
      if (payload == null) return null;
      syncCertificatesSession(payload);
      return payload;
    });

  const seedMissingStandardCertificates = async (dbCerts) => {
    if (!serviceList.length) return dbCerts;

    const dbNames = new Set(
      (dbCerts ?? []).map((cert) =>
        String(cert?.name ?? "")
          .trim()
          .toLowerCase(),
      ),
    );
    const missingTemplates = STANDARD_CERTIFICATES.filter(
      (template) =>
        !dbNames.has(
          String(template?.name ?? "")
            .trim()
            .toLowerCase(),
        ),
    );
    if (!missingTemplates.length) return dbCerts;

    await Promise.all(
      missingTemplates.map((template) =>
        dispatch(
          createClinicCertificateWoLoader(
            buildSeedPayloadFromTemplate(template, serviceList),
          ),
        ),
      ),
    );

    const res = await dispatch(getClinicCertificates());
    const nextPayload = res?.payload ?? dbCerts;
    syncCertificatesSession(nextPayload);
    return nextPayload;
  };

  useEffect(() => {
    if (fetchRef.current) return;
    fetchRef.current = true;
    onApiCall();
  }, []);

  useEffect(() => {
    if (!certsFetched || seedingRef.current || !serviceList.length) {
      return;
    }

    seedingRef.current = true;
    seedMissingStandardCertificates(certificatesSource).finally(() => {
      seedingRef.current = false;
    });
  }, [certsFetched, certificatesSource, serviceList]);

  const sourceServiceOptions = useMemo(() => {
    const defaults = [
      formValues?.rabiesSourceService,
      formValues?.parasiteSourceService,
    ].filter(Boolean);
    const byValue = new Map();
    [...serviceList, ...defaults].forEach((opt) => {
      if (opt?.value != null && !byValue.has(String(opt.value))) {
        byValue.set(String(opt.value), opt);
      }
    });
    return Array.from(byValue.values());
  }, [
    serviceList,
    formValues?.rabiesSourceService,
    formValues?.parasiteSourceService,
  ]);

  const isStandard = formValues?.kind === "standard";
  const isCreate = view === "editor" && !formValues?.id && !isStandard;
  const isTravelExport =
    formValues?.id === "std-travel-export" ||
    findStandardTemplate(formValues?.name)?.id === "std-travel-export";

  const persistCustom = (list) => {
    setCustomCerts(list);
    saveCustomCertificates(list);
  };

  const openCreate = () => {
    setFormValues({ ...initForm });
    setInsertField(null);
    setView("editor");
  };

  const openEdit = (cert) => {
    setFormValues({
      id: cert?.id ?? null,
      certificateId: cert?.certificateId ?? null,
      name: cert?.name ?? "",
      linkedServices: cert?.linkedServices ?? [],
      note: cert?.note ?? cert?.footerNote ?? DEFAULT_FOOTER_NOTE,
      body: cert?.body ?? "",
      kind: cert?.kind ?? "custom",
      subtitle: cert?.subtitle ?? "Not linked to a service",
      rabiesSourceService: cert?.rabiesSourceService ?? null,
      parasiteSourceService: cert?.parasiteSourceService ?? null,
    });
    setInsertField(null);
    setView("editor");
  };

  const onBackToList = () => {
    setView("list");
    setFormValues(initForm);
    setInsertField(null);
  };

  const handleFormChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleLinkedServicesChange = (value) => {
    const next = value ?? [];
    setFormValues((prev) => ({
      ...prev,
      linkedServices: next,
      subtitle:
        next.length > 0
          ? next
              .map((service) => service?.name ?? service?.serviceName)
              .filter(Boolean)
              .join(", ")
          : "Not linked to a service",
    }));
  };

  const handleInsertField = (field) => {
    setInsertField(field);
    if (!field?.value || isStandard) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      handleFormChange("body", `${formValues?.body ?? ""}${field.value}`);
      return;
    }
    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();
    editor.insertText(index, field.value);
    editor.setSelection(index + field.value.length);
  };

  const onPreview = (cert) => {
    const source =
      cert ??
      (formValues?.id || formValues?.name
        ? {
            id: formValues?.id,
            name: formValues?.name,
            kind: formValues?.kind,
            body: formValues?.body,
            note: formValues?.note,
            footerNote: formValues?.note,
            linkedServices: formValues?.linkedServices,
            rabiesSourceService: formValues?.rabiesSourceService,
            parasiteSourceService: formValues?.parasiteSourceService,
          }
        : null);
    if (!source) return;
    setPreviewCert(source);
    setPreviewOpen(true);
  };

  const onClosePreview = () => {
    setPreviewOpen(false);
    setPreviewCert(null);
  };

  const onSave = () => {
    const name = (formValues?.name ?? "").trim();
    if (!name) return;

    const payload = buildCertificatePayload(formValues, isTravelExport);
    const certificateId = formValues?.certificateId;

    const onSuccess = (res) => {
      if (!res?.payload) return;
      onApiCall();
      onBackToList();
    };

    if (certificateId) {
      dispatch(
        updateClinicCertificateById({ certificateId, data: payload }),
      ).then(onSuccess);
      return;
    }

    dispatch(createClinicCertificate(payload)).then(onSuccess);
  };

  const renderCertRow = (cert) => {
    const Icon = cert?.Icon ?? DescriptionOutlinedIcon;
    const isCustom = cert?.kind === "custom";
    return (
      <div className="cert-row" key={cert?.certificateId ?? cert?.id}>
        <div className="cert-icon-box">
          <Icon sx={{ color: AppColors.appPrimary, width: 22, height: 22 }} />
        </div>
        <div className="cert-row-main">
          <div className="cert-row-title-line">
            <span className="cert-row-name">{cert?.name}</span>
            <span
              className={`cert-badge ${
                isCustom ? "cert-badge-custom" : "cert-badge-standard"
              }`}
            >
              {isCustom ? "CUSTOM" : "STANDARD"}
            </span>
          </div>
          <div className="cert-row-sub">{cert?.note ?? ""}</div>
        </div>
        <div className="cert-row-actions">
          <CustomButton
            text="Preview"
            smallBtn
            whiteBtn
            onClick={() => onPreview(cert)}
          />
          <CustomButton text="Edit" smallBtn onClick={() => openEdit(cert)} />
        </div>
      </div>
    );
  };

  if (view === "editor") {
    return (
      <Container maxWidth="xl">
        <div className="cert-page">
          <div className="cert-card-wrap">
            <div className="cert-header-row">
              <Typography className="cert-title">
                {isCreate ? "Create Custom Certificate" : "Edit Certificate"}
              </Typography>
              <div className="flex-row-ali-cen">
                <div>
                  <CustomButton
                    text="Preview"
                    smallBtn
                    whiteBtn
                    onClick={() => onPreview()}
                  />
                </div>
                <div className="ml10">
                  <CustomButton text="Save" smallBtn onClick={onSave} />
                </div>
              </div>
            </div>

            <div className="mb16">
              <div className="cert-field-label">CERTIFICATE NAME</div>
              <CustomTextField
                fullWidth
                value={formValues?.name}
                handleChange={(e) => handleFormChange("name", e?.target?.value)}
                disabled={isStandard}
              />
            </div>

            <div className="mb16">
              <div className="cert-field-label mt10">LINKED SERVICE(S)</div>
              <SearchableSelect
                list={serviceList}
                value={formValues?.linkedServices ?? []}
                handleChange={(e) =>
                  handleLinkedServicesChange(e?.target?.value ?? [])
                }
                multiSelectTagCheck
                maxFilteredOptions={100}
                searchFields={[
                  "name",
                  "serviceName",
                  "category",
                  "subCategory",
                ]}
                backgroundColor={AppColors.white}
              />
              {isStandard ? (
                <span className="cert-badge-locked">
                  STANDARD — STRUCTURE LOCKED
                </span>
              ) : null}
            </div>

            {isTravelExport ? (
              <>
                <div className="mb16">
                  <div className="cert-field-label mt10">
                    RABIES VACCINATION — SOURCE SERVICE
                  </div>
                  <CustomSelect
                    list={sourceServiceOptions}
                    value={formValues?.rabiesSourceService}
                    handleChange={(e, value) =>
                      handleFormChange("rabiesSourceService", value)
                    }
                    search
                    fullWidth
                  />
                </div>
                <div className="mb16">
                  <div className="cert-field-label mt10">
                    PARASITE TREATMENT — SOURCE SERVICE
                  </div>
                  <CustomSelect
                    list={sourceServiceOptions}
                    value={formValues?.parasiteSourceService}
                    handleChange={(e, value) =>
                      handleFormChange("parasiteSourceService", value)
                    }
                    search
                    fullWidth
                  />
                </div>
              </>
            ) : null}

            {!isStandard ? (
              <div className="mb10">
                <div className="cert-field-label">CERTIFICATE BODY</div>
                <div className="cert-editor-toolbar-extra">
                  <div style={{ minWidth: 180 }}>
                    <CustomSelect
                      list={CERT_INSERT_FIELDS}
                      value={insertField}
                      handleChange={(e, value) => handleInsertField(value)}
                      search
                      fullWidth
                      label="+ Insert field..."
                    />
                  </div>
                </div>
                <div className="cert-body-wrap">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={formValues?.body}
                    onChange={(val) => handleFormChange("body", val)}
                  />
                </div>
              </div>
            ) : null}

            <div className="mb16">
              <div className="cert-field-label mt10">NOTE</div>
              <CustomTextField
                fullWidth
                value={formValues?.note}
                handleChange={(e) => handleFormChange("note", e?.target?.value)}
                multiline
              />
            </div>

            {/* <div className="mt20">
              <CustomButton
                text="Back"
                smallBtn
                whiteBtn
                onClick={onBackToList}
              />
            </div> */}
          </div>
        </div>
        <CertificatePreviewModal
          open={previewOpen}
          cert={previewCert}
          onClose={onClosePreview}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <div className="cert-page">
        <div className="cert-card-wrap">
          <div className="cert-header-row">
            <Typography className="cert-title">Certificates</Typography>
            {/* <div>
              <CustomButton
                text="+ Create Custom Certificate"
                smallBtn
                onClick={openCreate}
              />
            </div> */}
          </div>

          <div className="cert-section-label">STANDARD LIBRARY</div>
          <div className="cert-list">
            {certificateList.length > 0 ? (
              certificateList.map((cert) => renderCertRow(cert))
            ) : (
              <div className="cert-linked-empty">No certificates available</div>
            )}
          </div>

          {/* <div className="cert-section-label" style={{ marginTop: 22 }}>
            CUSTOM CERTIFICATES
          </div>
          {customCerts?.length > 0 ? (
            <div className="cert-list">
              {customCerts.map((cert) =>
                renderCertRow({
                  ...cert,
                  Icon: DescriptionOutlinedIcon,
                }),
              )}
            </div>
          ) : null} */}

          {/* <button
            type="button"
            className="cert-create-dashed"
            onClick={openCreate}
          >
            + Create Custom Certificate
          </button> */}
        </div>
      </div>
      <CertificatePreviewModal
        open={previewOpen}
        cert={previewCert}
        onClose={onClosePreview}
      />
    </Container>
  );
};

export default Certificates;
